import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { Category } from '@adgate/common/types/category.type';
import { Language } from '@adgate/common/types/language.type';
import { CommercialOffice } from '@adgate/common/types/commercial-office.type';
import { Agency } from '@adgate/common/types/agency.type';
import { forkJoin } from 'rxjs';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from "@adgate/common/pipes/object-desc-pipe";
import { ChannelFamily } from '@adgate/common/types/channel-family.type';
import { CommercialFilter } from '@adgate/modules/tv/types/commercial-filter.type';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import moment from 'moment';

@Component({
  selector: 'app-tv-commercial-filters-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    ObjectDescPipe
  ],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: AdGateConstants.ADGATE_DATE_FORMATS }
  ],
  templateUrl: './tv-commercial-filters-dialog-component.html',
  styleUrl: './tv-commercial-filters-dialog-component.scss',
})
export class TvCommercialFiltersDialogComponent implements OnInit
{
  private dialogRef: MatDialogRef<TvCommercialFiltersDialogComponent> = inject(MatDialogRef<TvCommercialFiltersDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private commonObjectService: CommonObjectService = inject(CommonObjectService);
  private dialogService: DialogService = inject(DialogService);
  private currentFilter: CommercialFilter = inject<CommercialFilter>(MAT_DIALOG_DATA);

  public comparatorService: ComparatorService = inject(ComparatorService);

  filtersForm!: FormGroup;

  categories: Category[] = [];
  channelFamilies: ChannelFamily[] = [];
  languages: Language[] = [];
  commercialOffices: CommercialOffice[] = [];
  agencies: Agency[] = [];

  constructor() 
  {
      this.filtersForm = this.fb.group({
        brand: "",
        product: "",
        referenceTitle: "",
        category: [],
        channelFamily: [],
        language: [[]],
        commercialOffice: [],
        agency: [],
        createdFrom: "",
        createdTo: "",
        referenceCode: "",
        resultLimit: 100
      });
  }

  ngOnInit(): void 
  {
    forkJoin([
      this.commonObjectService.getCategories(),
      this.commonObjectService.getCommercialOffices(null),
      this.commonObjectService.getAgencies(null),
      this.commonObjectService.getLanguages(),
      this.commonObjectService.getChannelFamilies(ChannelFamily.USAGE_TV)

    ]).subscribe({
      next: (responses:RESTApplicationResponse[]) => {
        this.categories = responses[0].data;
        this.commercialOffices = responses[1].data;
        this.agencies = responses[2].data;
        this.languages = responses[3].data;
        this.channelFamilies = responses[4].data;

        this.filtersForm.get('brand')?.setValue( this.currentFilter.brand );
        this.filtersForm.get('product')?.setValue( this.currentFilter.product );
        this.filtersForm.get('referenceTitle')?.setValue( this.currentFilter.version );
        this.filtersForm.get('category')?.setValue( this.currentFilter.category );
        this.filtersForm.get('channelFamily')?.setValue( this.currentFilter.channelFamily );
        this.filtersForm.get('language')?.setValue( this.currentFilter.language );
        this.filtersForm.get('commercialOffice')?.setValue( this.currentFilter.office );
        this.filtersForm.get('agency')?.setValue( this.currentFilter.agency );
        this.filtersForm.get('createdFrom')?.setValue( this.currentFilter.createdFrom ? moment(this.currentFilter.createdFrom) : null );
        this.filtersForm.get('createdTo')?.setValue( this.currentFilter.createdTo ? moment(this.currentFilter.createdTo) : null );
        this.filtersForm.get('referenceCode')?.setValue( this.currentFilter.referenceCode );
        this.filtersForm.get('resultLimit')?.setValue( this.currentFilter.limit );
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onCancel(): void 
  {
    this.dialogRef.close(null);
  }

  onClear(): void 
  {
    this.filtersForm.reset({ resultLimit: 100 });
  }

  onApply(): void 
  {
    this.currentFilter.brand = this.filtersForm.get('brand')?.value;
    this.currentFilter.product = this.filtersForm.get('product')?.value;
    this.currentFilter.version = this.filtersForm.get('referenceTitle')?.value;
    this.currentFilter.category = this.filtersForm.get('category')?.value;
    this.currentFilter.channelFamily = this.filtersForm.get('channelFamily')?.value;
    this.currentFilter.language = this.filtersForm.get('language')?.value;
    this.currentFilter.office = this.filtersForm.get('commercialOffice')?.value;
    this.currentFilter.agency = this.filtersForm.get('agency')?.value;
    this.currentFilter.referenceCode = this.filtersForm.get('referenceCode')?.value;
    this.currentFilter.limit = this.filtersForm.get('resultLimit')?.value;

    const createdFrom = this.filtersForm.get('createdFrom')?.value;
    const createdTo = this.filtersForm.get('createdTo')?.value;
    this.currentFilter.createdFrom = createdFrom ? createdFrom.unix() * 1000 : null;
    this.currentFilter.createdTo = createdTo ? createdTo.unix() * 1000 : null;

    this.dialogRef.close(this.currentFilter);
  }
}
